// Голямата търсачка „намери и добави" на работния лист „Доставка" — custom
// падащ списък с подравнени колони: Продукт (highlight) · Наличност · Посл.
// доставка · Ед. цена; при избран доставчик данните са само от неговите
// доставки.
import { useEffect } from "react";
import { Input } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { LastPrice, Product } from "../../../../api/inventory";
import { NegativeStockMarker } from "../../components/NegativeStockMarker";
import {
  DROPDOWN_GRID_CLASS,
  formatUnitPrice,
  formatQtyInput,
  highlightWords,
} from "./deliveryWorksheetShared";

// Един ред от резултатите — родителят добавя наличност/посл. цена/added флага
export type DeliverySearchRow = {
  product: Product;
  added: boolean;
  available: number;
  lastPrice: LastPrice | null;
  decimals: number;
};

type Props = {
  attachInputRef: (node: HTMLInputElement | null) => void;
  value: string;
  onValueChange: (value: string) => void;
  onFocusChange: (focused: boolean) => void;
  onInputKeyDown: (e: React.KeyboardEvent) => void;
  dropdownOpen: boolean;
  rows: DeliverySearchRow[];
  searchWords: string[];
  supplierName: string | null; // избран доставчик → хедърът показва името му
  supplierSelected: boolean;
  highlightedIndex: number;
  onHighlightChange: (index: number) => void;
  onPick: (productId: number) => void;
  // Продуктът липсва в справочника → създава се направо оттук (org базата)
  onCreateProduct: () => void;
};

export const DeliveryWorksheetSearch = ({
  attachInputRef,
  value,
  onValueChange,
  onFocusChange,
  onInputKeyDown,
  dropdownOpen,
  rows,
  searchWords,
  supplierName,
  supplierSelected,
  highlightedIndex,
  onHighlightChange,
  onPick,
  onCreateProduct,
}: Props) => {
  const { t } = useTranslation();

  // Маркираният резултат винаги остава във видимата част на списъка
  useEffect(() => {
    if (!dropdownOpen) return;
    document
      .getElementById(`dwx-opt-${highlightedIndex}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex, dropdownOpen]);

  return (
    <div className="[font-family:system-ui]">
      <div className="relative">
        <Input
          ref={(node) => attachInputRef(node?.input ?? null)}
          allowClear
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          onFocus={() => onFocusChange(true)}
          onBlur={() => onFocusChange(false)}
          onKeyDown={onInputKeyDown}
          prefix={
            <SearchOutlined className="mr-1 text-[17px] text-muted" />
          }
          placeholder={t("inventoryPage.wsSearchPh")}
          // ! — antd cssinjs е извън Tailwind layer-ите и иначе бие класа
          className="w-full !rounded-[10px] !text-[15px]"
        />
        {dropdownOpen && (
          <div className="dk-elevated absolute inset-x-0 top-[calc(100%+6px)] z-30 overflow-hidden rounded-xl border border-shade-3 bg-surface shadow-[0_18px_44px_rgba(20,15,8,0.16),0_4px_12px_rgba(20,15,8,0.08)]">
            <div
              className={`${DROPDOWN_GRID_CLASS} items-center gap-[18px] border-b border-shade-2 bg-fill px-[18px] py-2 text-[10.5px] font-semibold uppercase leading-[normal] tracking-[0.07em] text-muted`}
            >
              <span className="truncate">
                {supplierSelected
                  ? t("inventoryPage.wsPricesFromSupplier", {
                      name: supplierName ?? "",
                    })
                  : t("inventoryPage.wsResultsCount", { count: rows.length })}
              </span>
              <span className="whitespace-nowrap text-right">
                {t("inventoryPage.productOptStock")}
              </span>
              <span className="whitespace-nowrap text-right">
                {t("inventoryPage.productOptLastDelivery")}
              </span>
              <span className="whitespace-nowrap text-right">
                {t("inventoryPage.fieldUnitPrice")}
              </span>
            </div>
            <div className="max-h-[min(46vh,430px)] overflow-y-auto">
              {rows.map((row, index) => (
                <div
                  key={row.product.id}
                  id={`dwx-opt-${index}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onPick(row.product.id)}
                  onMouseMove={() => onHighlightChange(index)}
                  className={`${DROPDOWN_GRID_CLASS} cursor-pointer items-center gap-[18px] border-b border-shade-1 px-[18px] py-[11px] ${
                    index === highlightedIndex ? "bg-brand-tint" : "bg-surface"
                  }`}
                >
                  {/* ПРОДУКТ */}
                  <div className="flex min-w-0 items-center gap-2.5">
                    <span className="truncate text-[15px] font-semibold leading-[normal] text-ink">
                      {highlightWords(row.product.name, searchWords)}
                    </span>
                    {row.product.unit && (
                      <span className="shrink-0 text-xs leading-[normal] text-muted">
                        {row.product.unit}
                      </span>
                    )}
                    {row.added && (
                      <span className="shrink-0 rounded-full border border-brand-tint2 bg-brand-tint px-[9px] pb-[3px] pt-[2px] text-[11px] font-semibold leading-[normal] text-brand-deep-ink">
                        ✓ {t("inventoryPage.wsAddedTag")}
                      </span>
                    )}
                  </div>
                  {/* НАЛИЧНОСТ */}
                  <div className="whitespace-nowrap text-right font-[ui-monospace,Menlo,monospace] text-[13.5px] font-semibold leading-[normal] text-body">
                    {formatQtyInput(Math.max(0, row.available), row.decimals)}
                    {row.product.unit ? (
                      <span className="font-sans text-xs font-normal text-muted">
                        {" "}
                        {row.product.unit}
                      </span>
                    ) : null}
                    {/* минусът от продажби — под клампнатата нула */}
                    <NegativeStockMarker
                      quantity={row.available}
                      decimals={row.decimals}
                      className="block font-sans text-[11px] font-semibold"
                    />
                  </div>
                  {/* ПОСЛ. ДОСТАВКА */}
                  <div className="whitespace-nowrap text-right">
                    {row.lastPrice != null ? (
                      <span className="font-[ui-monospace,Menlo,monospace] text-[13.5px] leading-[normal] text-body">
                        {[
                          row.lastPrice.date
                            ? dayjs(row.lastPrice.date).format("DD.MM.YYYY")
                            : null,
                          row.lastPrice.quantity != null
                            ? `${formatQtyInput(row.lastPrice.quantity, row.decimals)}${row.product.unit ? ` ${row.product.unit}` : ""}`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" · ") || "—"}
                      </span>
                    ) : (
                      <span className="text-[12.5px] italic leading-[normal] text-faint">
                        {supplierSelected
                          ? t("inventoryPage.wsNoSupplierDelivery")
                          : t("inventoryPage.wsNoLastDelivery")}
                      </span>
                    )}
                  </div>
                  {/* ЕД. ЦЕНА */}
                  <div
                    className={`whitespace-nowrap text-right font-[ui-monospace,Menlo,monospace] text-[15px] font-bold leading-[normal] ${
                      row.lastPrice != null ? "text-brand-deep-ink" : "text-ghost"
                    }`}
                  >
                    {row.lastPrice != null
                      ? formatUnitPrice(row.lastPrice.unitPrice)
                      : "—"}
                  </div>
                </div>
              ))}
              {rows.length === 0 && (
                // „Няма го" → „Нов продукт" с написаното име. `onMouseDown` с
                // preventDefault пази фокуса на търсачката — иначе blur-ът
                // затваря списъка преди click-а.
                <div className="flex flex-wrap items-center gap-3 px-4 py-3.5 text-[12.5px] leading-[normal] text-muted">
                  {t("inventoryPage.wsNoMatch")}
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={onCreateProduct}
                    className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-dashed border-shade-4 bg-transparent px-2.5 py-1.5 text-[12.5px] font-semibold text-ink hover:border-brand hover:text-brand-deep-ink"
                  >
                    <PlusOutlined className="text-[11px]" />
                    {t("inventoryPage.wsCreateNamed", { name: value.trim() })}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryWorksheetSearch;
